import { defineSlotRecipe } from '@chakra-ui/react';
import { BorderWidths, Colors, Radii, Shadows } from '../../types';

const thumbBase = {
  bg: Colors.TEXT_ON_PRIMARY,
  boxShadow: Shadows.XS,
  outline: '1px solid',
  outlineColor: Colors.BORDER_ELEVATED,
  transitionProperty: 'translate',
  transitionDuration: 'ultra-fast',
  transitionTimingFunction: 'ease-in',
  boxSize: 'calc(var(--switch-track-height) - 2px)',
  _checked: {
    translate: 'var(--switch-x) 0',
  },
  _disabled: {
    bg: Colors.TEXT_DISABLED,
    boxShadow: Shadows.NONE,
  },
};

const controlBase = {
  position: 'relative',
  width: 'var(--switch-track-width)',
  height: 'var(--switch-track-height)',
  bg: Colors.SURFACE_NEUTRAL_MUTED,
  boxShadow: Shadows.INNER,
  borderRadius: Radii.FULL,
  borderWidth: BorderWidths.XS,
  borderColor: Colors.BORDER_INPUT,
  boxSizing: 'border-box',
  padding: '0px',
  _hover: {
    bg: Colors.ICON_NEUTRAL,
  },
  _active: {
    bg: Colors.SURFACE_NEUTRAL,
  },
  _disabled: {
    bg: Colors.TRANSPARENT,
    borderColor: Colors.SURFACE_DISABLED,
    _hover: {
      bg: Colors.TRANSPARENT,
      borderColor: Colors.SURFACE_DISABLED,
    },
  },
  _checked: {
    bg: Colors.SURFACE_PRIMARY,
    borderColor: Colors.BORDER_ELEVATED,
    _hover: {
      bg: Colors.SURFACE_PRIMARY_HOVER,
    },
    _active: {
      bg: Colors.SURFACE_PRIMARY_ACTIVE,
    },
    _disabled: {
      bg: Colors.SURFACE_DISABLED,
    },
  },
  _focusVisible: {
    outline: '2px solid',
    outlineColor: Colors.BORDER_FOCUS,
    outlineOffset: '2px',
    boxShadow: 'none',
  },
};

// `--switch-track-*` custom properties are set per size on the root and consumed by
// control/thumb.
export const switchSlotRecipe = defineSlotRecipe({
  slots: ['root', 'label', 'control', 'thumb', 'indicator', 'hiddenInput'],
  base: {
    root: {
      display: 'inline-flex',
      alignItems: 'center',
      lineHeight: 'inherit',
      position: 'relative',
      '--switch-diff': 'calc(var(--switch-track-width) - var(--switch-track-height))',
      '--switch-x': {
        base: 'var(--switch-diff)',
        _rtl: 'calc(var(--switch-diff) * -1)',
      },
    },
    thumb: thumbBase,
    control: controlBase,
  },
  variants: {
    // Chakra's switch defaults to variant `solid`, whose colorPalette track fill and scaled-down thumb beat
    // `base`, so `solid` restates `base` to keep the brand track colour and full-size thumb.
    variant: {
      solid: {
        control: controlBase,
        thumb: thumbBase,
      },
    },
    size: {
      sm: { root: { '--switch-track-width': '1.75rem', '--switch-track-height': '1rem' } },
      md: { root: { '--switch-track-width': '2rem', '--switch-track-height': '1.25rem' } },
      lg: { root: { '--switch-track-width': '2.5rem', '--switch-track-height': '1.5rem' } },
    },
  },
  defaultVariants: {
    size: 'md',
    variant: 'solid',
  },
});
